import { goBack, previousRoute, routePath, type Route } from '../platform/router.js';
import { routeBackLabel } from '../platform/route-labels.js';

/**
 * THE BACK CHIP (0.1.524) — returns to the screen this one was opened from (`goBack`), and says so.
 *
 * `fallback`/`fallbackLabel` are the route's own parent (its `from`/`origin`, or the tab it belongs
 * to): where a FRESH visit goes — a link, a scan, a reload in a new tab — when no in-app screen is
 * behind this one. Hoisted from the hand-rolled `.chip.back-chip` anchors every detail screen
 * carried (a fact repeated eight times: one rule now decides where back goes).
 *
 * Read at render, not memoised: the screen re-renders on every route change, which is the only
 * time "what is behind" can change.
 */
export function useBackTarget(fallback: Route, fallbackLabel: string): { href: string; label: string; go: () => void } {
  const behind = previousRoute();
  return {
    href: routePath(behind ?? fallback),
    label: behind === null ? fallbackLabel : routeBackLabel(behind),
    go: () => goBack(fallback),
  };
}

export function BackChip({
  fallback,
  fallbackLabel,
  className,
}: {
  fallback: Route;
  fallbackLabel: string;
  className?: string | undefined;
}): JSX.Element {
  const back = useBackTarget(fallback, fallbackLabel);
  return (
    <a
      className={className ?? 'chip back-chip'}
      href={back.href}
      onClick={(e) => {
        e.preventDefault();
        back.go();
      }}
    >
      {back.label}
    </a>
  );
}
